import React, { useEffect, useState, useRef, useContext } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useHistory, useLocation } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import cn from 'classnames'
import { ModalContext } from 'modaaals'
import qs from 'query-string'
import { SimpleLayout as Layout } from '../Layout'

import SelectionContext from '../../context/SelectionContext'
import { MUTATION } from '../../apollo'
import { Loader as LoaderIcon } from '../Icons'
import styles from './login.module.css'

function Login() {
  let history = useHistory()
  let location = useLocation()

  const { openModal } = useContext(ModalContext)
  const { selectionState } = useContext(SelectionContext)
  const firstInputRef = useRef(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [responseMessage, setResponseMessage] = useState(null)
  const [responseEmail, setResponseEmail] = useState(null)

  const [resetPassword] = useMutation(MUTATION.RESET_PASSWORD, {
    onError: (error) => {
      setErrorMessage(error.graphQLErrors[0].message)
    },
    onCompleted: (data) => {
      const resetPassword = data?.resetPassword
      if (resetPassword?.ok) {
        setResponseMessage(resetPassword.message)
        if (resetPassword.email) {
          setResponseEmail(resetPassword.email)
        }
      }
    },
  })

  useEffect(() => {
    if (firstInputRef && firstInputRef.current) {
      firstInputRef.current.focus()
    }
  }, [])

  async function onSubmit({ password, repeatedPassword }, { resetForm }) {
    try {
      const { token } = qs.parse(location.search)
      if (token) {
        await resetPassword({
          variables: { password, repeatedPassword, token },
        })
      } else {
        setErrorMessage('Reset Password request not valid')
      }
    } catch (err) {
      // setErrorMessage('Email not found')
    } finally {
      resetForm()
    }
  }

  function onFocus() {
    if (errorMessage) {
      setErrorMessage(null)
    }
  }

  return (
    <Layout className={styles.wrapper}>
      <div className={styles.innerWrapper}>
        <div
          className={styles.leftSide}
          style={{
            backgroundImage: `url(${require(`../../images/walkman.jpg`)})`,
          }}
        >
          <h3 className={cn(styles.maintitle, styles.leftTitle)}>
            The Definitive
            <br /> Musical Resource
          </h3>
        </div>
        <div className={styles.rightSide}>
          {responseMessage ? (
            <AnimatePresence>
              {responseMessage && (
                <motion.p
                  className={styles.form}
                  animate={{
                    opacity: 1,
                  }}
                  initial={{
                    opacity: 0,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  onAnimationComplete={() => {
                    setTimeout(() => {
                      setResponseMessage(null)
                      history.replace('/login', { email: responseEmail })
                    }, 4300)
                  }}
                >
                  {responseMessage}
                  <br />
                  <br />
                  <LoaderIcon className={styles.loaderIcon} /> Redirecting to
                  Login Page.
                </motion.p>
              )}
            </AnimatePresence>
          ) : (
            <Formik
              onSubmit={onSubmit}
              initialValues={{
                password: '',
                repeatedPassword: '',
              }}
              validationSchema={Yup.object().shape({
                password: Yup.string().required('Required'),
                repeatedPassword: Yup.string()
                  .oneOf([Yup.ref('password'), null])
                  .required('Required'),
              })}
            >
              {({ handleSubmit, errors }) => {
                return (
                  <form className={styles.form} onSubmit={handleSubmit}>
                    <h3 className={styles.maintitle}>Reset Password</h3>
                    <label htmlFor="password" className={styles.label}>
                      Password<sup>*</sup>{' '}
                      <AnimatePresence>
                        {errorMessage && (
                          <motion.div
                            className={styles.inputError}
                            animate={{
                              opacity: 1,
                            }}
                            initial={{
                              opacity: 0,
                            }}
                            exit={{
                              opacity: 0,
                            }}
                          >
                            {errorMessage}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </label>

                    <Field
                      type="password"
                      name="password"
                      id="password"
                      className={cn(styles.input, {
                        [styles.error]: errors.password,
                      })}
                      placeholder="Password"
                      innerRef={firstInputRef}
                      onFocus={onFocus}
                    />
                    <label htmlFor="repeatedPassword" className={styles.label}>
                      Repeat Password<sup>*</sup>{' '}
                    </label>
                    <Field
                      type="password"
                      name="repeatedPassword"
                      id="repeatedPassword"
                      className={cn(styles.input, {
                        [styles.error]: errors.repeatedPassword,
                      })}
                      placeholder="Repeat Password"
                      onFocus={onFocus}
                    />
                    <button className={styles.button}>Reset Password</button>

                    <p>
                      Login with your email to access your personalized music
                      sync dashboard with music selection, project information
                      and quote management.
                    </p>
                    <p
                      style={{
                        lineHeight: 2.5,
                        marginTop: -5,
                      }}
                    >
                      To create a profile account please send us a <br />
                      <button
                        className={styles.extraButton}
                        onClick={() => {
                          if (Object.keys(selectionState).length === 0) {
                            openModal({
                              type: 'supervision',
                              asEmptyQuote: true,
                            })
                          } else {
                            openModal({ type: 'quote', selectionState })
                          }
                        }}
                      >
                        Quote
                      </button>{' '}
                      or{' '}
                      <button
                        className={styles.extraButton}
                        onClick={() => {
                          openModal({ type: 'supervision' })
                        }}
                      >
                        Supervision
                      </button>{' '}
                      request.
                    </p>
                  </form>
                )
              }}
            </Formik>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default Login
